import { buildAIPrompt, SYMPTOM_NAMES } from './aiPromptTemplate';

/**
 * Build context summary from male wellness survey answers
 */
export function buildMaleContext(answers) {
    const contextParts = [];

    // Safety check
    if (answers.safety) {
        if (answers.safety.includes('none')) {
            contextParts.push('- Good health condition, no specific risks');
        } else {
            contextParts.push('- Some health concerns to note');
        }
    }

    // Demographics
    if (answers.demographics) {
        const { age, weight } = answers.demographics;
        if (age) contextParts.push(`- Age group: ${age}`);
        if (weight) contextParts.push(`- Weight: ${weight}`);
    }

    // Main concern
    if (answers.mainConcern) {
        const concernMap = {
            'energy': 'Declining energy/vitality',
            'sleep': 'Poor sleep quality',
            'stress': 'High stress',
            'overall': 'Overall conditioning'
        };
        contextParts.push(`- Main concern: ${concernMap[answers.mainConcern]}`);
    }

    // 分支A：体力精力
    if (answers.energyIssues && answers.energyIssues.length > 0) {
        const issues = answers.energyIssues.map(issue => {
            switch (issue) {
                case 'fatigue': return 'Easily fatigued';
                case 'lower_back': return 'Weak lower back/knees';
                case 'slow_recovery': return 'Slow recovery';
                case 'morning_tired': return 'Poor state in morning';
                default: return issue;
            }
        });
        contextParts.push(`- Energy issues: ${issues.join(', ')}`);
    }

    if (answers.energyDuration) {
        const durationMap = {
            '1-3months': 'Lasted 1-3 months',
            '3-6months': 'Lasted 3-6 months',
            '>6months': 'Lasted over 6 months'
        };
        contextParts.push(`- ${durationMap[answers.energyDuration]}`);
    }

    if (answers.lowerBackDetails && answers.lowerBackDetails.length > 0) {
        const details = answers.lowerBackDetails.map(d => {
            switch (d) {
                case 'back_pain': return 'Lower back pain';
                case 'knee_weak': return 'Weak knees';
                case 'worse_sitting': return 'Worse when sitting';
                case 'weather': return 'Affected by weather';
                default: return d;
            }
        });
        contextParts.push(`- Lower back/knees: ${details.join(', ')}`);
    }

    // 分支B：睡眠
    if (answers.sleepIssues && answers.sleepIssues.length > 0) {
        const issues = answers.sleepIssues.map(issue => {
            switch (issue) {
                case 'difficulty_falling': return 'Difficulty falling asleep';
                case 'light_sleep': return 'Light sleep';
                case 'sleep_tired': return 'Tired after sleep';
                case 'dreams': return 'Many dreams';
                default: return issue;
            }
        });
        contextParts.push(`- Sleep issues: ${issues.join(', ')}`);
    }

    if (answers.sleepDuration) {
        const durationMap = {
            '1-3months': 'Sleep issues for 1-3 months',
            '3-6months': 'Sleep issues for 3-6 months',
            '>6months': 'Sleep issues for over 6 months'
        };
        contextParts.push(`- ${durationMap[answers.sleepDuration]}`);
    }

    if (answers.sleepImpact && answers.sleepImpact.length > 0) {
        const impacts = answers.sleepImpact.map(i => {
            switch (i) {
                case 'poor_focus': return 'Poor focus';
                case 'fatigue': return 'Easily fatigued';
                case 'irritable': return 'Irritable';
                case 'low_energy': return 'Low energy';
                default: return i;
            }
        });
        contextParts.push(`- Daytime impact: ${impacts.join(', ')}`);
    }

    // 分支C：压力
    if (answers.stressSources && answers.stressSources.length > 0) {
        const sources = answers.stressSources.map(s => {
            switch (s) {
                case 'work': return 'Work stress';
                case 'family': return 'Family stress';
                case 'anxiety': return 'Anxiety/tension';
                case 'sleep_stress': return 'Affects sleep';
                default: return s;
            }
        });
        contextParts.push(`- Stress sources: ${sources.join(', ')}`);
    }

    if (answers.stressDuration) {
        const durationMap = {
            '1-3months': 'Stress for 1-3 months',
            '3-6months': 'Stress for 3-6 months',
            '>6months': 'Stress for over 6 months'
        };
        contextParts.push(`- ${durationMap[answers.stressDuration]}`);
    }

    if (answers.stressImpact && answers.stressImpact.length > 0) {
        const impacts = answers.stressImpact.map(i => {
            switch (i) {
                case 'tense': return 'Mentally tense';
                case 'fatigue': return 'Easily fatigued';
                case 'sleep': return 'Affects sleep';
                case 'appetite': return 'Appetite changes';
                default: return i;
            }
        });
        contextParts.push(`- Stress impact: ${impacts.join(', ')}`);
    }

    // 分支D：整体调理
    if (answers.overallGoals && answers.overallGoals.length > 0) {
        const goals = answers.overallGoals.map(g => {
            switch (g) {
                case 'prevent_decline': return 'Prevent energy decline';
                case 'boost_immunity': return 'Boost immunity';
                case 'mental': return 'Improve mental state';
                case 'exercise': return 'Support exercise';
                default: return g;
            }
        });
        contextParts.push(`- Goals: ${goals.join(', ')}`);
    }

    if (answers.overallStatus) {
        const statusMap = {
            'good': 'Good state, prevention',
            'slight_decline': 'Slight decline',
            'obvious_decline': 'Obvious decline'
        };
        contextParts.push(`- Current status: ${statusMap[answers.overallStatus]}`);
    }

    // Constitution
    if (answers.constitution && answers.constitution.length > 0) {
        const constitution = answers.constitution
            .filter(c => c !== 'none')
            .map(c => {
                switch (c) {
                    case 'cold_limbs': return 'Cold hands and feet';
                    case 'sweating': return 'Easily sweating';
                    case 'dry_mouth': return 'Dry mouth';
                    case 'heat': return 'Prone to inflammation';
                    default: return c;
                }
            });
        if (constitution.length > 0) {
            contextParts.push(`- Constitution: ${constitution.join(', ')}`);
        }
    }

    // Other symptoms
    if (answers.otherSymptoms && answers.otherSymptoms.length > 0) {
        const symptoms = answers.otherSymptoms
            .filter(s => s !== 'none')
            .map(s => {
                switch (s) {
                    case 'lower_back_pain': return 'Weak lower back/knees';
                    case 'dizziness': return 'Dizziness/tinnitus';
                    case 'frequent_urination': return 'Frequent urination';
                    case 'hair_loss': return 'Hair loss';
                    default: return s;
                }
            });
        if (symptoms.length > 0) {
            contextParts.push(`- Other symptoms: ${symptoms.join(', ')}`);
        }
    }

    // Lifestyle
    if (answers.lifestyle && answers.lifestyle.length > 0) {
        const habits = answers.lifestyle
            .filter(h => h !== 'healthy')
            .map(h => {
                switch (h) {
                    case 'alcohol': return 'Drink alcohol often';
                    case 'smoking': return 'Smoke';
                    case 'late_sleep': return 'Stay up late';
                    case 'exercise': return 'Regular exercise';
                    case 'sedentary': return 'Sedentary';
                    default: return h;
                }
            });
        if (habits.length > 0) {
            contextParts.push(`- Lifestyle: ${habits.join(', ')}`);
        }
    }

    // Family planning
    if (answers.familyPlanning) {
        const planMap = {
            'planning_now': 'Trying to conceive now',
            'planning_soon': 'Plan to conceive within a year',
            'no_plan': 'No plan to conceive'
        };
        contextParts.push(`- Family planning: ${planMap[answers.familyPlanning]}`);
    }

    return contextParts.join('\n');
}

/**
 * Build AI prompt for male wellness questions
 */
export function buildMalePrompt(question, answers, userName) {
    const context = buildMaleContext(answers);
  return buildAIPrompt(SYMPTOM_NAMES.male, context, question.text, userName);
}